/**
 * Dev-gated logger.
 *
 * `log` and `warn` are no-ops in production so operational chatter (payloads,
 * claims, subscription status) stays out of end-user DevTools. `error` always
 * fires so genuine failures surface in monitoring.
 *
 * `error` also forwards to Sentry when Sentry is initialized — this is the
 * single integration point that gives every existing `logger.error` call
 * site automatic error reporting without touching any of them.
 */
import * as Sentry from '@sentry/react';

const isDev = import.meta.env.DEV;

type LogArgs = unknown[];

const reportToSentry = (level: 'error' | 'warning', args: LogArgs): void => {
  // Silently no-op if Sentry isn't initialized (e.g. local dev without a DSN).
  // @sentry/react doesn't expose a public `isInitialized` so we check the
  // current hub's client.
  const client = Sentry.getClient();
  if (!client) return;

  const err = args.find((a) => a instanceof Error) as Error | undefined;
  const restAsContext = args
    .filter((a) => !(a instanceof Error))
    .map((a) => (typeof a === 'string' ? a : safeStringify(a)))
    .join(' ');

  if (err) {
    Sentry.captureException(err, {
      level,
      extra: restAsContext ? { context: restAsContext } : undefined,
    });
  } else {
    const message = restAsContext || 'logger.' + level + ' called';
    Sentry.captureMessage(message, { level });
  }
};

const safeStringify = (v: unknown): string => {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

export const logger = {
  log: (...args: LogArgs) => {
    if (isDev) console.log(...args);
  },
  warn: (...args: LogArgs) => {
    if (isDev) console.warn(...args);
    // Don't ship every warning to Sentry — too noisy. Promote to .error
    // when you actually want a Sentry event.
  },
  error: (...args: LogArgs) => {
    console.error(...args);
    reportToSentry('error', args);
  },
  debug: (...args: LogArgs) => {
    if (isDev) console.debug(...args);
  },
};
